import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { IBatchTemplateDTO } from '@app/core/models/batch-template/batch-template.model';
import { IBatchTemplateActivityDTO } from '@app/core/models/batch-template/batch-template-activity.model';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import { Guid } from 'guid-typescript';
import { forkJoin, Observable, of, map } from 'rxjs';
import { environment } from '@environment/environment';
import { KendoGridService } from '@app/core/services/custom-services/kendo-grid.service';
import { DialogService } from '@progress/kendo-angular-dialog';
import { BatchTemplateActivitiesDialogComponent } from '@app-module-operative/batch-template-activities-dialog/batch-template-activities-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class BatchTemplateService {
  private readonly kendoGridService = inject(KendoGridService);
  private readonly dialogService = inject(DialogService);

  constructor(private readonly http: HttpClient) {}

  getbatchTemplates(state: State, templateName: string): Observable<GridDataResult> {
    return this.http.get<GridDataResult>(`${environment.apiUrl}operative/batch-template`, {
      params: new HttpParams()
        .set('templateName', templateName.toString())
        .set('skip', String(state.skip))
        .set('take', String(state.take)),
    });
  }

  getbatchTemplatesByMachineId(state: State, id: number): Observable<GridDataResult> {
    let params = this.kendoGridService.getHttpParamsFilter(state);
    return this.http.get<GridDataResult>(`${environment.apiUrl}operative/batch-template/${id}`, {
      params: new HttpParams()
        .set('searchString', params.get('templateName') || '')
        .set('skip', String(state.skip))
        .set('take', String(state.take)),
    });
  }

  getbatchTemplatesByMachineIds(
    state: State,
    machineIds: number[],
  ): Observable<IBatchTemplateDTO[]> {
    if (!machineIds || machineIds.length === 0) {
      return of([]);
    }

    const requests = machineIds.map((id) => this.getbatchTemplatesByMachineId(state, id));

    return forkJoin(requests).pipe(
      map((results) => {
        const allTemplates: IBatchTemplateDTO[] = [];
        const seenIds = new Set<string>();

        results.forEach((result) => {
          if (result.data) {
            result.data.forEach((template: IBatchTemplateDTO) => {
              const templateId = template.batchTemplateId?.toString();
              if (templateId && !seenIds.has(templateId)) {
                seenIds.add(templateId);
                allTemplates.push(template);
              }
            });
          }
        });

        return allTemplates;
      })
    );
  }

  save(data: IBatchTemplateDTO): Observable<any> {
    return this.http.post(`${environment.apiUrl}operative/batch-template`, data);
  }

  update(data: IBatchTemplateDTO): Observable<any> {
    return this.http.put(`${environment.apiUrl}operative/batch-template`, data);
  }

  remove(batchTemplateId: Guid): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}operative/batch-template/${batchTemplateId}`);
  }

  getActivitiesByBatchTemplateId(batchTemplateId: Guid, state: State): Observable<IBatchTemplateActivityDTO[]> {
    let params = this.kendoGridService.getHttpParamsFilter(state);
    return this.http.get<IBatchTemplateActivityDTO[]>(
      `${environment.apiUrl}operative/batch-template/activities/${batchTemplateId}`,
      { params }
    );
  }

  openActivitiesDialog(dataItem: IBatchTemplateDTO): void {
    if (dataItem.activityCount <= 0) return;

    const dialogRef = this.dialogService.open({
      content: BatchTemplateActivitiesDialogComponent,
      width: 1000,
      height: 'auto',
      themeColor: 'primary',
    });

    const instance = dialogRef.content.instance as BatchTemplateActivitiesDialogComponent;
    instance.data = {
      batchTemplateId: dataItem.batchTemplateId,
      templateName: dataItem.templateName,
    };
  }
}
